
const formatShortAddress = (data) => {
  const addr = data?.address || {};

  const calle =
    addr.road ||
    addr.pedestrian ||
    addr.footway ||
    addr.neighbourhood ||
    addr.suburb ||
    addr.hamlet ||
    addr.locality ||
    '';

  const comuna =
    addr.city ||
    addr.town ||
    addr.village ||
    addr.municipality ||
    addr.county ||
    '';

  return {
    shortAddress: [calle, comuna].filter(Boolean).join(', ') || data?.display_name || 'Mi ubicación',
    comuna
  };
};

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, CalendarDays, Home, TreePine, Dog } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { es } from 'date-fns/locale';
import { format } from 'date-fns';

const SERVICE_TABS = [
  { id: 'paseo', label: 'Paseo', icon: Dog },
  { id: 'cuidado', label: 'Cuidado', icon: Home },
  { id: 'daycare', label: 'Daycare', icon: TreePine },
];

export default function SearchBar({ onSearch, initialService, initialAddress }) {
  const navigate = useNavigate();

  const [activeService, setActiveService] = useState(initialService || 'paseo');
  const [address, setAddress] = useState(initialAddress || '');
  const [locationLoading, setLocationLoading] = useState(false);
  const [dateRange, setDateRange] = useState({ from: undefined, to: undefined });
  const [selectedDates, setSelectedDates] = useState([]);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [locationSource, setLocationSource] = useState('auto');

  useEffect(() => {
    const savedAddress = localStorage.getItem('ucan_user_address');

    if (!initialAddress && savedAddress) {
      setAddress(savedAddress);
      setLocationSource('location');
      return;
    }

    if (!navigator.geolocation || initialAddress) return;

    setLocationLoading(true);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;

        try {
          localStorage.setItem('ucan_user_lat', latitude);
          localStorage.setItem('ucan_user_lng', longitude);

          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1`
          );

          const data = await res.json();

          if (data?.display_name) {
            const { shortAddress, comuna } = formatShortAddress(data);

            setAddress(shortAddress);
            setLocationSource('location');
            localStorage.setItem('ucan_user_address', shortAddress);

            if (comuna) localStorage.setItem('ucan_user_comuna', comuna);
          }
        } catch (e) {
          console.error(e);
        } finally {
          setLocationLoading(false);
        }
      },
      () => setLocationLoading(false),
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 600000,
      }
    );
  }, [initialAddress]);

  const getDateLabel = () => {
    if (activeService === 'cuidado') {
      if (dateRange.from && dateRange.to) {
        return `${format(dateRange.from, 'dd MMM', { locale: es })} - ${format(dateRange.to, 'dd MMM', { locale: es })}`;
      }
      if (dateRange.from) return `${format(dateRange.from, 'dd MMM', { locale: es })} - ...`;
      return 'Ingresa un rango de fechas';
    }

    if (selectedDates.length > 0) {
      if (selectedDates.length === 1) {
        return format(selectedDates[0], 'dd MMM yyyy', { locale: es });
      }
      return `${selectedDates.length} fechas seleccionadas`;
    }

    return 'Selecciona una o mas fechas';
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const params = new URLSearchParams();

    const cleanAddress = address.trim();
    const savedAddress = localStorage.getItem('ucan_user_address') || '';
    const lat = localStorage.getItem('ucan_user_lat');
    const lng = localStorage.getItem('ucan_user_lng');

    const isSavedLocation =
      cleanAddress &&
      savedAddress &&
      cleanAddress === savedAddress;

    if (cleanAddress) {
      params.set('comuna', cleanAddress);
    }

    if (isSavedLocation && lat && lng) {
      params.set('source', 'location');
      params.set('lat', lat);
      params.set('lng', lng);
    } else {
      params.set('source', 'comuna');
    }

    params.set('service', activeService);

    if (activeService === 'cuidado' && dateRange.from) {
      params.set('from', dateRange.from.toISOString());
      if (dateRange.to) {
        params.set('to', dateRange.to.toISOString());
      }
    } else if (selectedDates.length > 0) {
      params.set(
        'dates',
        selectedDates.map((d) => d.toISOString()).join(',')
      );
    }

    if (onSearch) {
      onSearch({
        service: activeService,
        address,
        dateRange,
        selectedDates,
      });
    } else {
      navigate(`/search?${params.toString()}`);
    }
  };

  return (
    <div className="w-full">
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        {SERVICE_TABS.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeService === tab.id;

          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => {
                setActiveService(tab.id);
                setDateRange({ from: undefined, to: undefined });
                setSelectedDates([]);
              }}
              className={`flex items-center gap-2 px-5 py-3 rounded-xl font-semibold ${
                isActive
                  ? 'bg-[#E6202E] text-white'
                  : 'bg-white border border-gray-200'
              }`}
            >
              <Icon className="w-5 h-5" />
              {tab.label}
            </button>
          );
        })}
      </div>

      <form
        onSubmit={handleSubmit}
        className="bg-white rounded-2xl shadow-lg border border-gray-200 p-3"
      >
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1 relative">
            <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={address}
              placeholder={
                locationLoading
                  ? 'Obteniendo tu ubicación...'
                  : 'Dirección'
              }
              onChange={(e) => { setAddress(e.target.value); setLocationSource('comuna'); }}
              className="w-full pl-12 pr-4 h-16 border rounded-xl"
            />
          </div>

          <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="flex-1 flex items-center gap-3 px-4 h-16 border rounded-xl text-left"
              >
                <CalendarDays className="w-5 h-5 text-gray-400" />
                <span>{getDateLabel()}</span>
              </button>
            </PopoverTrigger>

            <PopoverContent className="w-auto p-0">
              {activeService === 'cuidado' ? (
                <Calendar
                  mode="range"
                  selected={dateRange}
                  onSelect={(r) =>
                    setDateRange(r || { from: undefined, to: undefined })
                  }
                  locale={es}
                  numberOfMonths={2}
                />
              ) : (
                <Calendar
                  mode="multiple"
                  selected={selectedDates}
                  onSelect={(d) => setSelectedDates(d || [])}
                  locale={es}
                  numberOfMonths={2}
                />
              )}
            </PopoverContent>
          </Popover>

          <Button
            type="submit"
            className="h-16 px-10 bg-[#E6202E] hover:bg-[#D31522]"
          >
            Buscar
          </Button>
        </div>
      </form>
    </div>
  );
}
